/* ===================================================================
   useFeedbackBoard() + mutations -- native feature-request board
   (HOPPER + CORBATO, pre-launch Step 2, 2026-07-14).

   Read:   GET   /api/v1/feedback-board/posts     (sorted by upvotes)
   Create: POST  /api/v1/feedback-board/posts
   Vote:   POST  /api/v1/feedback-board/posts/{id}/vote   (toggle, one/client)
   Edit:   PATCH /api/v1/feedback-board/posts/{id}         (own post)

   The Feedback screen binds to these. After any write we invalidate so the
   list re-sorts by the new vote tally. Read hook disabled until a Bearer rr_
   token exists (so it never auto-fires a 401).
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type FeedbackPost,
  type FeedbackPostsResponse,
  type FeedbackVoteResponse,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useFeedbackBoard(): UseQueryResult<FeedbackPostsResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.feedbackBoard(),
    queryFn: ({ signal }) => api.listFeedbackPosts(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

export interface UseCreateFeedbackPostResult {
  create: (input: { title: string; body?: string }) => Promise<FeedbackPost>;
  isPending: boolean;
  error: Error | null;
}

export function useCreateFeedbackPost(): UseCreateFeedbackPostResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: { title: string; body?: string }) =>
      api.createFeedbackPost(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.feedbackBoard() });
    },
  });
  return {
    create: (input) => m.mutateAsync(input).then((r) => r.post),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}

export interface UseToggleFeedbackVoteResult {
  toggle: (id: string) => Promise<FeedbackVoteResponse>;
  pendingId: string | null;
  error: Error | null;
}

export function useToggleFeedbackVote(): UseToggleFeedbackVoteResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (id: string) => api.toggleFeedbackVote(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.feedbackBoard() });
    },
  });
  return {
    toggle: (id) => m.mutateAsync(id),
    pendingId: m.isPending ? (m.variables ?? null) : null,
    error: m.error ?? null,
  };
}

export interface UseUpdateFeedbackPostResult {
  update: (id: string, patch: { title?: string; body?: string }) => Promise<FeedbackPost>;
  pendingId: string | null;
  error: Error | null;
}

export function useUpdateFeedbackPost(): UseUpdateFeedbackPostResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: { title?: string; body?: string } }) =>
      api.updateFeedbackPost(id, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.feedbackBoard() });
    },
  });
  return {
    update: (id, patch) => m.mutateAsync({ id, patch }).then((r) => r.post),
    pendingId: m.isPending ? (m.variables?.id ?? null) : null,
    error: m.error ?? null,
  };
}
